import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { required, email, length } from 'redux-form-validators';
import { isValidNumber } from 'libphonenumber-js';

import { setNonAuthorized, setAuthorized } from 'auth/login/_redux';
import { Agreements, ReduxTextField, ReduxSelectField, PhoneField, DefaultButton, BasicModal } from 'common/components';
import { withTheme } from 'common/styling/theme';
import TelegramAuthWidget from '../../../_components/TelegramAuthWidget';

import { loginWithTelegramFromReg, registration, togglePartnerCodeAction, togglePromoCodeAction } from '../../_redux';

import { staticStyles, getDynamicStyles } from './style';

const validatePhoneNumber = phoneNumber => {
  if (!isValidNumber(phoneNumber)) {
    return <FormattedMessage id="validationErrorWrongPhone" />;
  }
};

function isPartnerCodeInvalid(partnerCode) {
  return Boolean(partnerCode) && !/^\S+$/.test(partnerCode);
}

const validate = values => {
  const errors = {};

  const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).+$/;
  if (!passwordRegex.test(values.password)) {
    errors.password = <FormattedMessage id="form.errors.passwordValidate" />;
  }

  if (values.passwordRepeat !== values.password) {
    errors.passwordRepeat = <FormattedMessage id="form.errors.passwordRepeat" />;
  }

  if (isPartnerCodeInvalid(values.partnerCode)) {
    errors.partnerCode = <FormattedMessage id="form.errors.partnerCode" />;
  }

  return errors;
};

function RegistrationForm({
  partnership,
  handleSubmit,
  pristine,
  submitting,
  invalid,
  error,
  partnerCodeIsOpen,
  promoCodeIsOpen,
  isCorporate,
  disclamer,
  requirePartnerCode,
  togglePartnerCode,
  togglePromoCode,
  theme,
  registrationSettings,
  promoCodes,
  customRegistrationFormOnebid,
  customRegistrationFormNameEmailCountryPassword,
  addCountryToRegistrationForm,
  initialValues,
  dispatch,
  isTelegramAuth,
  telegramData,
  loginWithTelegramFromReg,
  countries,
  countriesAreLoaded,
  personalIdInKycForm,
  selectedClientType,
  isConfirmPopupEnabled,
  intl,
  className,
  kycFormWithoutPhone,
}) {
  const [passwordFieldType, setPasswordFieldType] = useState('password');
  const [isConfimModalOpened, setIsConfirmModalOpened] = useState(false);

  const showPassword = () => {
    if (passwordFieldType === 'password') {
      setPasswordFieldType('text');
    } else {
      setPasswordFieldType('password');
    }
  };

  const handleConfirmModalToggle = () => {
    setIsConfirmModalOpened(isOpened => !isOpened);
  };

  useEffect(() => {
    if (!partnerCodeIsOpen && (initialValues.partnerCode || requirePartnerCode)) {
      togglePartnerCode();
    }
  }, []);

  const submit = handleSubmit(values => {
    const element = document.getElementById('registration-page-partner-code');
    let isPartnerCodeCustomlyHidden = false;
    if (element) {
      const style = window.getComputedStyle(element);
      isPartnerCodeCustomlyHidden = style.getPropertyValue('display') === 'none';
    }
    const { partnerCode, ...data } = values;
    let sendData = partnerCodeIsOpen ? { ...values, isPartnerCodeCustomlyHidden } : data;

    if (isTelegramAuth && telegramData) sendData = { ...sendData, ...telegramData };

    if (!promoCodes || !promoCodeIsOpen) {
      delete sendData.promoCode;
    }

    return registration({ ...sendData }, dispatch);
  });

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const handleTelegramAuth = data => loginWithTelegramFromReg(data);

  const countriesOptions = countries.map(option => ({
    value: option.gid,
    label: option.name,
  }));

  const clientTypeOptions = [
    { value: 'new', label: intl.formatMessage({ id: 'authPersonalAccount' }) },
    { value: 'corporate', label: intl.formatMessage({ id: 'authCorporateAccount' }) },
  ];

  return (
    <form id="main-registration-form" className={classNames('RegistrationForm', className)} onSubmit={submit}>
      {/* temp for tixee */}
      {window.location.hostname !== 'my.tixee.com' && disclamer?.enabled && disclamer?.subject && (
        <div className="disclamer__text">{disclamer.subject}</div>
      )}
      {isCorporate && (
        <Field
          className="RegistrationForm__select-client-type"
          component={ReduxSelectField}
          name="clientType"
          textId="justClientType"
          options={clientTypeOptions}
          disabled={submitting}
          validate={[required()]}
        />
      )}
      {!customRegistrationFormOnebid && (
        <div className="RegistrationForm__inputs-inner">
          <Field
            component={ReduxTextField}
            type="text"
            name="firstName"
            textId="justFirstName"
            validate={[required()]}
            disabled={submitting}
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="lastName"
            textId="justLastName"
            validate={[required()]}
            disabled={submitting}
          />
        </div>
      )}
      <Field
        component={ReduxTextField}
        type="email"
        name="email"
        textId={selectedClientType === 'corporate' ? 'authCompanyEmail' : 'authEmail'}
        validate={[required(), email()]}
        disabled={submitting}
      />
      {!customRegistrationFormOnebid && !kycFormWithoutPhone && !customRegistrationFormNameEmailCountryPassword && (
        <Field
          component={PhoneField}
          name="phone"
          textId="justPhone"
          validate={[required(), validatePhoneNumber]}
          disabled={submitting}
        />
      )}

      {(customRegistrationFormOnebid ||
        customRegistrationFormNameEmailCountryPassword ||
        addCountryToRegistrationForm) && (
        <Field
          className="RegistrationForm__select-country"
          component={ReduxSelectField}
          searchable
          name="countryGid"
          textId="justCountryResidence"
          options={countriesOptions}
          disabled={submitting || !countriesAreLoaded}
          validate={[required()]}
        />
      )}
      {personalIdInKycForm && !customRegistrationFormOnebid && !customRegistrationFormNameEmailCountryPassword && (
        <>
          <Field
            className="RegistrationForm__select-country"
            component={ReduxSelectField}
            searchable
            name="countryGid"
            textId="justCountryResidence"
            options={countriesOptions}
            disabled={submitting || !countriesAreLoaded}
            validate={[required()]}
          />

          <Field
            component={ReduxTextField}
            type="text"
            name="personalId"
            textId="kycPassportNumber"
            disabled={submitting}
            validate={[required()]}
          />
        </>
      )}
      {!isTelegramAuth && (
        <div className="RegistrationForm__inputs-inner">
          <Field
            component={ReduxTextField}
            type={passwordFieldType}
            name="password"
            textId="authPassword"
            validate={[required(), length({ min: 6 })]}
            disabled={submitting}
            action={showPassword}
            actionTooltipId={passwordFieldType === 'password' ? 'showPassword' : 'hidePassword'}
            actionClassName={passwordFieldType === 'password' ? 'show' : 'hide'}
          />
          <Field
            component={ReduxTextField}
            type="password"
            name="passwordRepeat"
            textId="authPasswordRepeat"
            validate={[required()]}
            disabled={submitting}
          />
        </div>
      )}
      {selectedClientType === 'corporate' && (
        <Field
          id="registration-page-company-name"
          component={ReduxTextField}
          type="text"
          name="companyName"
          textId="authCompanyName"
          validate={[required()]}
          disabled={submitting}
        />
      )}
      {partnership.enabled && partnerCodeIsOpen && (
        <Field
          id="registration-page-partner-code"
          component={ReduxTextField}
          type="text"
          name="partnerCode"
          textId="authPartnerCodeField"
          validate={[required()]}
          disabled={submitting}
        />
      )}
      {promoCodeIsOpen && promoCodes && (
        <Field
          id="registration-page-promo-code"
          component={ReduxTextField}
          type="text"
          name="promoCode"
          textId="authPromoCodeField"
          validate={[required()]}
          disabled={submitting}
        />
      )}
      {partnership.enabled && !requirePartnerCode && (
        <div id="registration-page-partner-code-inner" className="PartnerCode__inner">
          <FormattedMessage id={partnerCodeIsOpen ? 'authNoPartnerCode' : 'authPartnerCode'}>
            {txt => (
              <button type="button" onClick={togglePartnerCode}>
                {txt}
              </button>
            )}
          </FormattedMessage>
        </div>
      )}
      {promoCodes && (
        <div id="registration-page-promo-code-inner" className="PartnerCode__inner">
          <FormattedMessage id={promoCodeIsOpen ? 'authNoPromoCode' : 'authPromoCode'}>
            {txt => (
              <button type="button" onClick={togglePromoCode}>
                {txt}
              </button>
            )}
          </FormattedMessage>
        </div>
      )}
      <Agreements agreements={registrationSettings.agreements} disabled={submitting} />
      {/* temp for tixie */}
      {window.location.hostname === 'my.tixee.com' && disclamer?.enabled && disclamer?.subject && (
        <div className="disclamer__text disclamer__text--noMG">{disclamer.subject}</div>
      )}
      <DefaultButton
        id="registration-page-registration-button"
        textId="authDoRegister"
        type={isConfirmPopupEnabled ? 'button' : 'submit'}
        disabled={invalid || pristine || submitting}
        loading={submitting}
        filled
        onClick={isConfirmPopupEnabled ? handleConfirmModalToggle : null}
      />
      <TelegramAuthWidget handleTelegramAuth={handleTelegramAuth} />
      {error && <strong className="formError">{error}</strong>}
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx>{dynamicStyles}</style>

      {isConfirmPopupEnabled && (
        <BasicModal
          isOpen={isConfimModalOpened}
          onRequestClose={handleConfirmModalToggle}
          captionId="authCheckInformation">
          <div className="RegistrationForm__confirmPopup__content">
            <FormattedMessage id="authRegisterPopupConfirmationText" />
            <div className="RegistrationForm__confirmPopup__buttons">
              <DefaultButton textId="justGoBack" type="button" onClick={handleConfirmModalToggle} />
              <DefaultButton
                textId="justConfirmProceed"
                type="button"
                disabled={invalid || pristine || submitting}
                loading={submitting}
                filled
                onClick={submit}
              />
            </div>
          </div>
        </BasicModal>
      )}
    </form>
  );
}

RegistrationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  error: PropTypes.string,
  theme: PropTypes.object,
  partnership: PropTypes.object.isRequired,
  partnerCodeIsOpen: PropTypes.bool,
  promoCodeIsOpen: PropTypes.bool,
  disclamer: PropTypes.object,
  requirePartnerCode: PropTypes.bool.isRequired,
  togglePartnerCode: PropTypes.func.isRequired,
  togglePromoCode: PropTypes.func.isRequired,
  registrationSettings: PropTypes.object.isRequired,
  promoCodes: PropTypes.bool.isRequired,
  customRegistrationFormOnebid: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  countries: PropTypes.array,
  countriesAreLoaded: PropTypes.bool.isRequired,
  personalIdInKycForm: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    partnerCode: PropTypes.string,
  }),
  isTelegramAuth: PropTypes.bool.isRequired,
  telegramData: PropTypes.object.isRequired,
  loginWithTelegramFromReg: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  selectedClientType: PropTypes.string,
  isCorporate: PropTypes.bool,
  isConfirmPopupEnabled: PropTypes.bool,
  customRegistrationFormNameEmailCountryPassword: PropTypes.bool,
  addCountryToRegistrationForm: PropTypes.bool,
  className: PropTypes.string,
};

RegistrationForm.defaultProps = {
  error: '',
  selectedClientType: 'new',
  theme: {},
  disclamer: {},
  partnerCodeIsOpen: false,
  promoCodeIsOpen: false,
  initialValues: {},
  countries: [],
  isCorporate: false,
  isConfirmPopupEnabled: false,
  customRegistrationFormNameEmailCountryPassword: false,
  addCountryToRegistrationForm: false,
  className: '',
};

const selector = formValueSelector('registration');

export default compose(
  injectIntl,
  withTheme(),
  withRouter,
  connect(
    state => ({
      selectedClientType: selector(state, 'clientType'),
      registrationSettings: state.interfaceConfig.registration,
      partnership: state.interfaceConfig.partnership,
      partnerCodeIsOpen: state.auth.registration.partnerCodeIsOpen,
      promoCodeIsOpen: state.auth.registration.promoCodeIsOpen,
      requirePartnerCode: state.interfaceConfig.requirePartnerCode,
      disclamer: state.interfaceConfig.disclamer,
      personalIdInKycForm: state.interfaceConfig.features.includes('personalIdInKycForm'),
      promoCodes: state.interfaceConfig.features.includes('promoCodes'),
      customRegistrationFormOnebid: state.interfaceConfig.features.includes('customRegistrationFormOnebid'),
      customRegistrationFormNameEmailCountryPassword: state.interfaceConfig.features.includes(
        'customRegistrationFormNameEmailCountryPassword'
      ),
      addCountryToRegistrationForm: state.interfaceConfig.features.includes('addCountryToRegistrationForm'),
      isConfirmPopupEnabled: state.interfaceConfig.registrationConfirmationPopup,
      kycFormWithoutPhone: state.interfaceConfig.features.includes('kycFormWithoutPhone'),
    }),
    dispatch => ({
      togglePartnerCode: () => dispatch(togglePartnerCodeAction()),
      togglePromoCode: () => dispatch(togglePromoCodeAction()),
      loginWithTelegramFromReg: data => dispatch(loginWithTelegramFromReg.request(data)),
    })
  ),
  reduxForm({
    form: 'registration',
    validate,
    onSubmitSuccess: (
      result,
      dispatch,
      { history, isEmailConfirmationEnabled, displayThankBlock, displayTfaModal, isTfaRequired }
    ) => {
      // Этот элемент указывает, что регистрация прошла успешно
      const divElement = document.createElement('div');

      divElement.id = 'div-that-indicates-successful-registration';

      divElement.style.width = '1px';
      divElement.style.height = '1px';
      divElement.style.display = 'none !important';

      document.body.appendChild(divElement);

      if (isTfaRequired && !isEmailConfirmationEnabled) {
        displayTfaModal();
        dispatch(setNonAuthorized());
      }

      if (isEmailConfirmationEnabled) {
        displayThankBlock();
      }

      if (!isTfaRequired && !isEmailConfirmationEnabled) {
        dispatch(setAuthorized());
        history.push('/');
      }
    },
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
  })
)(RegistrationForm);
export { RegistrationForm };
